import React, {useCallback, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import {Link} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {Translation as T} from "@awe-web/shared/lib";
import {MenuLink} from "@awe-web/shared/lib";

import {TypeArea} from "./layout.js";
import Brand from "./brand.jsx";
import {MenuButton} from "./sidebar_menu.jsx";
import UserNavigation from "./user_navigation.jsx";
import {AppNavigation} from "./app_navigation.js";
import {LoadingSpinner} from "./loading_indicator.jsx";
import LanguageSwitch from "../l10n/components/language_switch.jsx";
import {Cms} from "../routing/routes.js";
import {setCurrentHeaderHeight} from "../dom/actions.js";
import {getCurrentHeaderHeight} from "../dom/store.js";
import {getCurrentViewportWidth, getCurrentScrollPosition} from "../window/store.js";
import {getFooterLinks} from "../l10n/store.js";
import { useStartScreenPath } from "../user/store.js";

import "./screen.scss";

export const Screen = (props) => {
  const {children} = props;
  const className = classnames("screen", props.className);

  return (
    <div className={className}>{children}</div>
  );
};
Screen.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
};

const SCROLL_TRESHOLD = 200;

export function ScreenHeader() {
  const dispatch = useDispatch();
  const viewportWidth = useSelector(getCurrentViewportWidth);
  const scrollPosition = useSelector(getCurrentScrollPosition);
  const headerHeight = useSelector(getCurrentHeaderHeight);
  
  const [isCollapsed, setIsCollapsed] = useState(false);
  const headerRef = useRef(null);

  const getHeaderHeight = useCallback(() => headerRef.current?.offsetHeight || 0, [headerRef]);

  useEffect(() => {
    dispatch(setCurrentHeaderHeight(getHeaderHeight()));
  }, [viewportWidth, dispatch, getHeaderHeight]);

  useEffect(() => {
    setIsCollapsed(scrollPosition >= SCROLL_TRESHOLD);
  }, [scrollPosition]);

  useEffect(() => {
    if (scrollPosition > SCROLL_TRESHOLD + headerHeight) {
      dispatch(setCurrentHeaderHeight(getHeaderHeight()));
    }
  }, [scrollPosition, headerHeight, dispatch, getHeaderHeight]);

  return (
    <header className="screen-header">
      <div ref={headerRef} className="screen-header__content">
        <TypeArea className="screen-header__main">
          <Brand />
          <UserNavigation className="screen-header__user-navigation" />
          <LanguageSwitch className="screen-header__language-switch" />
          <MenuButton className="screen-header__menu-button" /> 
        </TypeArea>

        <TypeArea>
          <AppNavigation collapsed={isCollapsed} className="screen-header__app-navigation" />
        </TypeArea>
      </div>

      <div className="screen-header__padding" style={{ paddingTop: headerHeight }} />
    </header>
  );
}

export function ScreenSection({children, main, light, dark, className}) {
  const combinedClassName = classnames("screen-section", className, {
    "screen-section--light": light,
    "screen-section--dark": dark
  });
  const Tag = main ? "main" : "section";

  return (
    <Tag className={combinedClassName}>{children}</Tag>
  );
}

ScreenSection.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  main: PropTypes.bool,
  dark: PropTypes.bool,
  light: PropTypes.bool
};

export const ScreenSubsection = (props) => {
  const {children} = props;
  const className = classnames("screen-subsection", props.className);

  return (
    <div className={className}>{children}</div>
  );
};
ScreenSection.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
};

const BOOK_RENT_OBJECT = "FSD0000137";
const LEGAL_NOTICE = "FSD0000145";
const IMPRESSUM = "FSD0000146";
const PRIVACY = "FSD0000147";
const COOKIES = "FSD0000148";

export function ScreenFooter() {
  const footerLinks = useSelector((state) => getFooterLinks(state));
  const startScreenPath = useStartScreenPath();

  return (
    <footer className="screen-footer">
      <nav className="screen-footer__content">
        <div className="screen-footer__promotional">
          <TypeArea className="screen-footer__promotional__content">
            <ul className="screen-footer__promotional__area">
              <li>
                <Link
                  className="screen-footer__promotional__link"
                  to={startScreenPath}
                >
                  <T code={BOOK_RENT_OBJECT} />
                </Link>
              </li>
              {footerLinks.toJS().map((link) => (
                <li key={link.id}>
                  <MenuLink
                    className="screen-footer__promotional__link"
                    pageName={link.pageName}
                    url={link.url}
                  >
                    {link.text}
                  </MenuLink>
                </li>
              ))}
            </ul>
          </TypeArea>
        </div>

        <div className="screen-footer__compliance">
          <TypeArea className="screen-footer__compliance__content">
            <ul className="screen-footer__compliance__area">
              <li><Link className="screen-footer__compliance__link" to={Cms.location({pageName: "legalnotice"})}><T code={LEGAL_NOTICE} /></Link></li>
              <li><Link className="screen-footer__compliance__link" to={Cms.location({pageName: "imprint"})}><T code={IMPRESSUM} /></Link></li>
              <li><Link className="screen-footer__compliance__link" to={Cms.location({pageName: "privacy"})}><T code={PRIVACY} /></Link></li>
              <li><Link className="screen-footer__compliance__link" to={Cms.location({pageName: "cookies"})}><T code={COOKIES} /></Link></li>
            </ul>
          </TypeArea>
        </div>
      </nav>
    </footer>
  );
}

export function ScreenHeading({className, children}) {
  const combinedClassName = classnames("screen-heading", className);

  return (
    <h1 className={combinedClassName}>{children}</h1>
  );
}

ScreenHeading.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
};

export function ScreenHeadingWithSupplement({children, heading, className}) {
  const combinedClassName = classnames("screen-heading-with-supplement", className);

  return (
    <div className={combinedClassName}>
      <ScreenHeading className="screen-heading-with-supplement__heading">
        {heading}
      </ScreenHeading>

      {children}
    </div>
  );
}

ScreenHeadingWithSupplement.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  heading: PropTypes.node
};

export const ScreenSectionHeading = (props) => {
  const {children, light} = props;
  const className = classnames("screen-section-heading", props.className, {
    "screen-section-heading--light": light
  });

  return (
    <h2 className={className}>{children}</h2>
  );
};
ScreenSectionHeading.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  light: PropTypes.bool
};

export const ScreenSectionSubheading = (props) => {
  const {children} = props;
  const className = classnames("screen-section-subheading", props.className);

  return (
    <h3 className={className}>{children}</h3>
  );
};
ScreenSectionSubheading.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
};

export const ScreenLoading = (props) => {
  return (
    <LoadingSpinner
      className="screen-loading"
    />
  );
};
ScreenLoading.propTypes = {
};
